import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { SelectDropDownModule } from 'ngx-select-dropdown';
import { ConsultationsService } from '../../../core/services/consultation/consultations.service';
import { CHRONIC_DISEASES } from '../../../main/constants/chronic-diseases';
import { MedicationType } from '../../../../enums/medication-type.enum';
import { InsulinAdministrationMethod } from '../../../../enums/insulin-administration-method.enum';
import { OTHER_MEDICATIONS_OTHERS } from '../../../main/constants/other-medications';
@Component({
  selector: 'app-disease-information-form',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TranslateModule,
    SelectDropDownModule,
  ],
  templateUrl: './disease-information-form.component.html',
  styleUrl: './disease-information-form.component.scss',
})
export class DiseaseInformationFormComponent implements OnInit {
  private readonly insulinTypeNames = ['NPH', 'Lente', 'Ultralente'];

  @Input() diseasesInformationForm!: FormGroup;
  @Input() maxDate: string;
  @Output() formSubmitted = new EventEmitter<any>();
  @Output() preventForbiddenCharactersEvent = new EventEmitter<{
    event: any;
    type: string;
  }>();
  @Output() nextStepEvent = new EventEmitter<void>();
  years: number[] = [];
  medicationConfig: any = {
    displayKey: 'name',
    valueKey: 'id',
    search: true,
    height: '200px',
    placeholder: 'İlaç Seçiniz',
    limitTo: 0,
    moreText: 'more',
  };

  diabetDiagnosisTypes: { id: string; text: string; value: boolean }[] = [
    { id: 'hasDiabetesYes', text: 'Evet', value: true },
    { id: 'hasDiabetesNo', text: 'Hayır', value: false },
  ];
  medicationForDiabetes: { id: string; text: string; value: number }[] = [
    {
      id: 'oralHypoglycemic',
      text: 'oralHypoglycemic',
      value: MedicationType.ORAL_HYPOGLYCEMIC,
    },
    { id: 'insulin', text: 'insulin', value: MedicationType.INSULIN },
    { id: 'none', text: 'no', value: MedicationType.NONE },
  ];
  diabetesTypes: { id: string; name: string }[] = [];
  insulinAdministrationRouteArr = [
    { id: InsulinAdministrationMethod.INJECTION, text: 'Injeksiyon' },
    { id: InsulinAdministrationMethod.PUMP, text: 'Pompa' },
  ];
  times: { id: string; text: string; value: number }[] = [
    { id: 'morning', text: 'Sabah', value: 1 },
    { id: 'noon', text: 'Öğlen', value: 2 },
    { id: 'evening', text: 'Akşam', value: 3 },
  ];
  insulinTableHeaders: string[] = [
    'time',
    'selected',
    'insulinType',
    'doseIu',
    'time',
  ];
  insulinTypes: { id: string; name: string }[] = [];
  oralHypoglycemicTableHeaders: string[] = [
    'time',
    'selected',
    'medicineName',
    'doseMg',
    'time',
  ];
  medicationList: { id: string; name: string }[] = [];
  chronicDiseases: string[] = CHRONIC_DISEASES;
  medicationType = MedicationType;
  otherMedicationId: string = OTHER_MEDICATIONS_OTHERS;
  constructor(
    private fb: FormBuilder,
    private consultationsService: ConsultationsService
  ) { }

  ngOnInit(): void {
    if (!this.diseasesInformationForm) {
      this.initForm();
    }
    this.getMedicalInformationsList();
    const currentYear = new Date().getFullYear();
    const startYear = 1900;
    for (let year = currentYear; year >= startYear; year--) {
      this.years.push(year);
    }
  }
  onYearSelected(event: Event): void {
    const fullDate = new Date(Date.UTC(Number(this.diseasesInformationForm.value.diagnosisDateView), 0, 1)); // 1 Ocak, saat 00:00:00 UTC
    this.diseasesInformationForm.get('diagnosisDate')?.setValue(fullDate.toISOString());
  }
  initForm(): void {
    this.diseasesInformationForm = this.fb.group({
      hasDiabetes: [null, Validators.required],
      diabetesTypeId: [null, Validators.required],
      diagnosisDate: [null],
      diagnosisDateView: [null],
      medicationType: [null],
      oralHypoglycemic: [false],
      insulin: [false],
      none: [false],
      medications: [null],
      hasHeartDisease: [false],
      hasKidneyDisease: [false],
      hasThyroidDisease: [false],
      morningNPH: [false],
      morningLente: [false],
      morningUltralente: [false],
      morningDose: [null],
      morningTime: [null],

      // Oral Hypoglycemic
      morningOralHypoglycemicSelected: [false],
      morningOralHypoglycemicMedicineName: [null],
      morningOralHypoglycemicDose: [null],
      morningOralHypoglycemicTime: [null],
      noonOralHypoglycemicSelected: [false],
      noonOralHypoglycemicMedicineName: [null],
      noonOralHypoglycemicDose: [null],
      noonOralHypoglycemicTime: [null],
      eveningOralHypoglycemicSelected: [false],
      eveningOralHypoglycemicMedicineName: [null],
      eveningOralHypoglycemicDose: [null],
      eveningOralHypoglycemicTime: [null],

      // Insulin
      insulinAdministrationMethod: [null],
      morningInsulinSelected: [null],
      morningInsulinNPH: [false],
      morningInsulinLente: [false],
      morningInsulinUltralente: [false],
      morningInsulinDose: [null],
      morningInsulinTime: [null],
      noonInsulinSelected: [null],
      noonInsulinNPH: [false],
      noonInsulinLente: [false],
      noonInsulinUltralente: [false],
      noonInsulinDose: [null],
      noonInsulinTime: [null],
      eveningInsulinSelected: [null],
      eveningInsulinNPH: [false],
      eveningInsulinLente: [false],
      eveningInsulinUltralente: [false],
      eveningInsulinDose: [null],
      eveningInsulinTime: [null],

      // Lab Information fields
      testDate: [null],
      timeOfDay: [null],
      hbA1cValue: [null],
      targetLowerLimit: [null],
      targetUpperLimit: [null],

      // Other Chronic Diseases
      hypertension: [false],
      cardiovascularDisease: [false],
      heartFailure: [false],
      hyperlipidemia: [false],
      kidneyFailure: [false],
      chronicPain: [false],
      majorDepression: [false],
      anxiety: [false],
      sleepDisorder: [false],
      physicalDisability: [false],
      other: [false],

      // Other Medications
      otherMed_morningSelected: [false],
      otherMed_morningName: [null],
      otherMed_morningDose: [null],
      otherMed_morningTime: [null],
      otherMed_morningOtherMedication: [null],
      otherMed_noonSelected: [false],
      otherMed_noonName: [null],
      otherMed_noonDose: [null],
      otherMed_noonTime: [null],
      otherMed_noonOtherMedication: [null],
      otherMed_eveningSelected: [false],
      otherMed_eveningName: [null],
      otherMed_eveningDose: [null],
      otherMed_eveningTime: [null],
      otherMed_eveningOtherMedication: [null],
      otherMed_otherSelected: [false],
      otherMed_otherName: [null],
      otherMed_otherDose: [null],
      otherMed_otherTime: [null],
      otherMed_otherOtherMedication: [null],
    });
  }

  getMedicalInformationsList(): void {
    this.consultationsService
      .getDiabetesPreparationPageInformations()
      .subscribe((res) => {
        this.medicationList = res?.medications;
        this.diabetesTypes = res?.diabetesTypes;
        this.insulinTypes = res?.insulinTypes;
      });
  }

  onChronicDiseaseChange(event: Event, value: number): void {
    const checked = (event.target as HTMLInputElement).checked;
    const control = this.diseasesInformationForm.get(
      'otherImportantChronicDiseases'
    );

    if (!control?.value) {
      control?.setValue([]);
    }

    if (checked) {
      control?.value.push(value);
    } else {
      const index = control?.value.indexOf(value);
      if (index > -1) {
        control?.value.splice(index, 1);
      }
    }
  }
  isInsulinSelected(timeId: string): boolean {
    return this.diseasesInformationForm.get(`${timeId}InsulinSelected`)?.value === true;
  }

  isOralHypoglycemicSelected(timeId: string): boolean {
    return (
      this.diseasesInformationForm.get(`${timeId}OralHypoglycemicSelected`)
        ?.value === true
    );
  }

  isInsulinTypeSelected(timeId: string, insulinTypeName: string): boolean {
    return this.diseasesInformationForm.get(
      `${timeId}Insulin${insulinTypeName}`
    )?.value === true;
  }

  onInsulinSelectionChange(timeId: string, isSelected: boolean): void {
    this.diseasesInformationForm
      .get(`${timeId}InsulinSelected`)
      ?.setValue(isSelected);

    if (!isSelected) {
      this.clearInsulinRow(timeId);
    }
  }

  onOralHypoglycemicSelectionChange(
    timeId: string,
    isSelected: boolean
  ): void {
    this.diseasesInformationForm
      .get(`${timeId}OralHypoglycemicSelected`)
      ?.setValue(isSelected);

    if (!isSelected) {
      this.clearOralHypoglycemicRow(timeId);
    }
  }

  onInsulinTypeChange(timeId: string, selectedTypeName: string): void {
    this.insulinTypeNames.forEach((typeName) => {
      this.diseasesInformationForm
        .get(`${timeId}Insulin${typeName}`)
        ?.setValue(typeName === selectedTypeName);
    });
  }

  getDoseUnitByMedicationType(isInsulin: boolean): 'IU' | 'mg' {
    return isInsulin ? 'IU' : 'mg';
  }

  goToNextStep(): void {
    if (this.diseasesInformationForm.invalid) {
      this.markFormGroupTouched(this.diseasesInformationForm);
      return;
    }
    this.nextStepEvent.emit();
  }

  // savePreparation(event: Event): void {
  //   event.preventDefault();

  //   const form = {
  //     hasDiabetes: this.diseasesInformationForm.get('hasDiabetes')?.value,
  //     diabetesTypeId: this.diseasesInformationForm.get('diabetesTypeId')?.value,
  //     diagnosisDate: this.diseasesInformationForm.get('diagnosisDate')?.value,
  //     medicationType: {
  //       oralHypoglycemic:
  //         this.diseasesInformationForm.get('oralHypoglycemic')?.value,
  //       insulin: this.diseasesInformationForm.get('insulin')?.value,
  //       none: this.diseasesInformationForm.get('none')?.value,
  //     },
  //     laboratoryInformation: {
  //       testDate: this.diseasesInformationForm.value.testDate,
  //       testTime: this.formatTime(
  //         this.diseasesInformationForm.get('timeOfDay')?.value
  //       ),
  //       hbA1CValue: this.diseasesInformationForm.get('hbA1CValue')?.value ?? 0,
  //       targetLowerLimit:
  //         this.diseasesInformationForm.get('targetLowerLimit')?.value ?? 0,
  //       targetUpperLimit:
  //         this.diseasesInformationForm.get('targetUpperLimit')?.value ?? 0,
  //     },
  //     chronicCondition: {
  //       hypertension: this.diseasesInformationForm.get('hypertension')?.value,
  //       cardiovascularDisease: this.diseasesInformationForm.get(
  //         'cardiovascularDisease'
  //       )?.value,
  //       heartFailure: this.diseasesInformationForm.get('heartFailure')?.value,
  //       hyperlipidemia:
  //         this.diseasesInformationForm.get('hyperlipidemia')?.value,
  //       kidneyFailure: this.diseasesInformationForm.get('kidneyFailure')?.value,
  //       chronicPain: this.diseasesInformationForm.get('chronicPain')?.value,
  //       majorDepression:
  //         this.diseasesInformationForm.get('majorDepression')?.value,
  //       anxiety: this.diseasesInformationForm.get('anxiety')?.value,
  //       sleepDisorder: this.diseasesInformationForm.get('sleepDisorder')?.value,
  //       physicalDisability:
  //         this.diseasesInformationForm.get('physicalDisability')?.value,
  //       other: this.diseasesInformationForm.get('other')?.value,
  //     },
  //     otherMedicationSchedules: [
  //       // Morning
  //       {
  //         timePeriod: 1,
  //         dosage: this.diseasesInformationForm.get('otherMed_morningDose')
  //           ?.value,
  //         time: this.formatTime(
  //           this.diseasesInformationForm.get('otherMed_morningTime')?.value
  //         ),
  //         usedMedications: [
  //           {
  //             medicationId: this.diseasesInformationForm.get(
  //               'otherMed_morningName'
  //             )?.value,
  //             otherMedication:
  //               this.diseasesInformationForm.get('otherMed_morningName')
  //                 ?.value === OTHER_MEDICATIONS_OTHERS
  //                 ? this.diseasesInformationForm.get(
  //                   'otherMed_morningOtherMedication'
  //                 )?.value
  //                 : null,
  //           },
  //         ],
  //       },
  //       // Noon
  //       {
  //         timePeriod: 2,
  //         dosage: this.diseasesInformationForm.get('otherMed_noonDose')?.value,
  //         time: this.formatTime(
  //           this.diseasesInformationForm.get('otherMed_noonTime')?.value
  //         ),
  //         usedMedications: [
  //           {
  //             medicationId:
  //               this.diseasesInformationForm.get('otherMed_noonName')?.value,
  //             otherMedication:
  //               this.diseasesInformationForm.get('otherMed_noonName')?.value ===
  //                 OTHER_MEDICATIONS_OTHERS
  //                 ? this.diseasesInformationForm.get(
  //                   'otherMed_noonOtherMedication'
  //                 )?.value
  //                 : null,
  //           },
  //         ],
  //       },
  //       // Evening
  //       {
  //         timePeriod: 3,
  //         dosage: this.diseasesInformationForm.get('otherMed_eveningDose')
  //           ?.value,
  //         time: this.formatTime(
  //           this.diseasesInformationForm.get('otherMed_eveningTime')?.value
  //         ),
  //         usedMedications: [
  //           {
  //             medicationId: this.diseasesInformationForm.get(
  //               'otherMed_eveningName'
  //             )?.value,
  //             otherMedication:
  //               this.diseasesInformationForm.get('otherMed_eveningName')
  //                 ?.value === OTHER_MEDICATIONS_OTHERS
  //                 ? this.diseasesInformationForm.get(
  //                   'otherMed_eveningOtherMedication'
  //                 )?.value
  //                 : null,
  //           },
  //         ],
  //       },
  //       // Other
  //       {
  //         timePeriod: 4,
  //         dosage: this.diseasesInformationForm.get('otherMed_otherDose')?.value,
  //         time: this.formatTime(
  //           this.diseasesInformationForm.get('otherMed_otherTime')?.value
  //         ),
  //         usedMedications: [
  //           {
  //             medicationId:
  //               this.diseasesInformationForm.get('otherMed_otherName')?.value,
  //             otherMedication:
  //               this.diseasesInformationForm.get('otherMed_otherName')
  //                 ?.value === OTHER_MEDICATIONS_OTHERS
  //                 ? this.diseasesInformationForm.get(
  //                   'otherMed_otherOtherMedication'
  //                 )?.value
  //                 : null,
  //           },
  //         ],
  //       },
  //     ],
  //   };

  //   this.formSubmitted.emit(form);
  // }

  isControlInvalid(controlName: string): boolean {
    const control = this.diseasesInformationForm.get(controlName);
    return control
      ? control.invalid && (control.dirty || control.touched)
      : false;
  }

  private markFormGroupTouched(formGroup: FormGroup) {
    Object.values(formGroup.controls).forEach((control) => {
      control.markAsTouched();
    });
  }

  preventForbiddenCharacters(event: any, type: string): void {
    this.preventForbiddenCharactersEvent.emit({ event, type });
  }

  onDateInputChange(value: string, type: 'testDate' | 'diagnosisDate'): void {
    const sanitizedValue = value.replace(/[^0-9]/g, '');
    const today = new Date();
    let formattedValue = '';

    if (sanitizedValue.length <= 2) {
      formattedValue = sanitizedValue;
    } else if (sanitizedValue.length <= 4) {
      formattedValue = `${sanitizedValue.slice(0, 2)}-${sanitizedValue.slice(
        2
      )}`;
    } else if (sanitizedValue.length <= 8) {
      formattedValue = `${sanitizedValue.slice(0, 2)}-${sanitizedValue.slice(
        2,
        4
      )}-${sanitizedValue.slice(4)}`;
    }

    const [day, month, year] = formattedValue
      .split('-')
      .map((part) => parseInt(part, 10));

    const currentYear = new Date().getFullYear();
    const minYear = currentYear - 145;
    if (
      formattedValue.startsWith('00') ||
      formattedValue.substring(3, 5) === '00' ||
      formattedValue.substring(6, 7) === '0' ||
      (day && day > 31) ||
      (month && month > 12) ||
      (year &&
        year.toString().length == 4 &&
        (year > currentYear || year < minYear))
    ) {
      if (type === 'testDate') {
        this.diseasesInformationForm.patchValue({
          testDate: '',
        });
      } else if (type === 'diagnosisDate') {
        this.diseasesInformationForm.patchValue({
          diagnosisDate: '',
        });
      }
      return;
    }

    const inputDate = new Date(year, month - 1, day);

    const todayFormatted = `${today.getDate().toString().padStart(2, '0')}-${(
      today.getMonth() + 1
    )
      .toString()
      .padStart(2, '0')}-${today.getFullYear()}`;

    if (sanitizedValue.length === 8 && inputDate > today) {
      if (type === 'testDate') {
        this.diseasesInformationForm.patchValue({
          testDate: todayFormatted,
        });
      } else if (type === 'diagnosisDate') {
        this.diseasesInformationForm.patchValue({
          diagnosisDate: todayFormatted,
        });
      }
    } else {
      if (type === 'testDate') {
        this.diseasesInformationForm.patchValue({
          testDate: formattedValue,
        });
      } else if (type === 'diagnosisDate') {
        this.diseasesInformationForm.patchValue({
          diagnosisDate: formattedValue,
        });
      }
    }
  }

  formatTime(timeString: string): string {
    return timeString + ':00';
  }

  onDiabetesMedicationChange(value: number): void {
    if (value === MedicationType.NONE) {
      this.diseasesInformationForm.patchValue({
        insulin: false,
        oralHypoglycemic: false,
      });
    } else {
      this.diseasesInformationForm.patchValue({
        none: false,
      });
    }
  }

  private clearInsulinRow(timeId: string): void {
    this.insulinTypeNames.forEach((typeName) => {
      this.diseasesInformationForm
        .get(`${timeId}Insulin${typeName}`)
        ?.setValue(false);
    });

    this.diseasesInformationForm.patchValue({
      [`${timeId}InsulinDose`]: null,
      [`${timeId}InsulinTime`]: null,
    });
  }

  private clearOralHypoglycemicRow(timeId: string): void {
    this.diseasesInformationForm.patchValue({
      [`${timeId}OralHypoglycemicMedicineName`]: null,
      [`${timeId}OralHypoglycemicDose`]: null,
      [`${timeId}OralHypoglycemicTime`]: null,
    });
  }
}
